<template>
  <div class="choose-company-page">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="header-container">
            <div class="header-content">
              <div class="header-text">
                <h2 class="font-weight-bold mb-1">
                  {{ isSwitching ? 'Switch Company' : 'Choose Company' }} | Farmbusiness
                </h2>
                <p class="text-gray mb-0">
                  {{ isSwitching ? 'Select a different company to switch to:' : 'Welcome ' + authUser.first_name + ' ' + authUser.last_name + '! Please select a company to continue.' }}
                </p>
              </div>
              <div class="header-actions">
                <am-button
                  icon="PlusIcon"
                  label="Add Company"
                  variant="primary"
                  class="mr-2"
                  @click="addNewCompany"
                />
                <b-link @click="goBack" class="btn btn-outline-primary">
                  <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                  {{ isSwitching ? 'Cancel' : 'Back to Login' }}
                </b-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- View Toggle -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="view-toggle-container">
            <div class="view-toggle">
              <span class="toggle-label" :class="{ active: !isRegionalView }">List View</span>
              <b-form-checkbox
                switch
                v-model="isRegionalView"
                class="toggle-switch"
              />
              <span class="toggle-label" :class="{ active: isRegionalView }">Regional View</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Filter -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="search-container">
                        <div class="search-input-wrapper">
              <am-input
                v-model="searchQuery"
                name="search"
                label=""
                :show-label="false"
                :show-errors="false"
                placeholder="Search companies by name, address, contact, or region..."
                prefix-icon="SearchIcon"
                :suffix-icon="searchQuery ? 'XIcon' : null"
                @input="onSearchInput"
                @onSuffixIconClick="clearSearch"
              />
            </div>
            <div v-if="searchQuery" class="search-results-info">
              <small class="text-muted">
                Showing {{ filteredCompanies.length }} of {{ originalCompanies.length }} companies
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Companies Grid -->
      <div v-if="!isRegionalView" class="companies-masonry">
        <div
          v-for="company in displayCompanies"
          :key="company.id"
          class="company-item"
        >
          <div class="company-card">
            <b-card>
              <div class="d-flex align-items-start mb-3">
                <am-avatar
                  size="60"
                  :src="company.logo"
                  class="mr-3"
                />
                <div class="flex-grow-1">
                  <h5 class="font-weight-bold mb-1">{{ company.name }}</h5>
                  <p class="text-gray small mb-1">{{ company.address }}</p>
                  <p class="text-gray small mb-1">
                    <feather-icon icon="PhoneIcon" size="14" class="mr-1" />
                    {{ company.contact_phone }}
                  </p>
                  <p class="text-gray small mb-0">
                    <feather-icon icon="MailIcon" size="14" class="mr-1" />
                    {{ company.email || 'N/A' }}
                  </p>
                  <div class="mt-1">
                    <b-badge :variant="company.status === 'active' ? 'success' : 'danger'">
                      {{ company.status === 'active' ? 'Active' : 'Inactive' }}
                    </b-badge>
                  </div>
                </div>
              </div>
              
              <!-- Action Buttons -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="d-flex align-items-center">
                  <am-button
                    icon-only
                    variant="flat-primary"
                    :icon="company.detailsShowing ? 'EyeOffIcon' : 'EyeIcon'"
                    @click="toggleCompanyDetails(company)"
                    tool-tip-text="View Details"
                    class="mr-2"
                  />
                  <am-button
                    icon-only
                    variant="flat-primary"
                    icon="EditIcon"
                    @click="editCompany(company)"
                    tool-tip-text="Edit Company"
                  />
                </div>
                <am-button
                  :label="isSwitching ? 'Switch' : 'Login'"
                  variant="primary"
                  :loading="loggingIn && selectedCompanyId === company.id"
                  @click="loginToCompany(company)"
                />
              </div>

              <!-- Company Details -->
              <b-collapse :id="`company-${company.id}`" v-model="company.detailsShowing" @show="onCompanyDetailsShow(company.id)" @hide="onCompanyDetailsHide(company.id)">
                <div class="company-details">
                  <div class="row">
                    <div class="col-md-6 mb-2">
                      <strong>Contact Name:</strong>
                      <span class="ml-1">{{ company.contact_name }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                      <strong>Country:</strong>
                      <span class="ml-1">{{ company.country_name }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                      <strong>Region:</strong>
                      <span class="ml-1">{{ company.region_name }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                      <strong>Contact Phone:</strong>
                      <span class="ml-1">{{ company.contact_phone }}</span>
                    </div>
                    <div class="col-md-6 mb-2" v-if="company.community">
                      <strong>Community:</strong>
                      <span class="ml-1">{{ company.community }}</span>
                    </div>
                    <div class="col-md-6 mb-2" v-if="company.secondary_contact">
                      <strong>Secondary Contact:</strong>
                      <span class="ml-1">{{ company.secondary_contact }}</span>
                    </div>
                    <div class="col-12 mb-2">
                      <strong>Address:</strong>
                      <span class="ml-1">{{ company.address }}</span>
                    </div>
                    <div class="col-12 mb-2" v-if="company.email">
                      <strong>Email:</strong>
                      <span class="ml-1">{{ company.email }}</span>
                    </div>
                    
                    <!-- Membership Information -->
                    <div class="col-12 mt-3 mb-2">
                      <h6 class="text-primary">Membership Information</h6>
                    </div>
                    <div class="col-md-3 mb-2" v-if="company.no_of_societies">
                      <strong>Societies:</strong>
                      <span class="ml-1">{{ company.no_of_societies }}</span>
                    </div>
                    <div class="col-md-3 mb-2" v-if="company.no_of_members">
                      <strong>Total Members:</strong>
                      <span class="ml-1">{{ company.no_of_members }}</span>
                    </div>
                    <div class="col-md-3 mb-2" v-if="company.no_of_males">
                      <strong>Male Members:</strong>
                      <span class="ml-1">{{ company.no_of_males }}</span>
                    </div>
                    <div class="col-md-3 mb-2" v-if="company.no_of_females">
                      <strong>Female Members:</strong>
                      <span class="ml-1">{{ company.no_of_females }}</span>
                    </div>
                    <div class="col-md-3 mb-2" v-if="company.no_of_youth">
                      <strong>Youth Members:</strong>
                      <span class="ml-1">{{ company.no_of_youth }}</span>
                    </div>
                    
                    <!-- Business Information -->
                    <div class="col-12 mt-3 mb-2">
                      <h6 class="text-primary">Business Information</h6>
                    </div>
                    <div class="col-md-6 mb-2" v-if="company.annual_revenue">
                      <strong>Annual Revenue:</strong>
                      <span class="ml-1">{{ company.annual_revenue }}</span>
                    </div>
                    <div class="col-md-6 mb-2" v-if="company.partner_organization">
                      <strong>Partner Organization:</strong>
                      <span class="ml-1">{{ company.partner_organization }}</span>
                    </div>
                    <div class="col-md-6 mb-2" v-if="company.sustainability_certification">
                      <strong>Certification:</strong>
                      <span class="ml-1">{{ company.sustainability_certification }}</span>
                    </div>
                    <div class="col-md-6 mb-2" v-if="company.date_established">
                      <strong>Date Established:</strong>
                      <span class="ml-1">{{ formatDate(company.date_established) }}</span>
                    </div>
                    <div class="col-md-6 mb-2" v-if="company.date_registered">
                      <strong>Date Registered:</strong>
                      <span class="ml-1">{{ formatDate(company.date_registered) }}</span>
                    </div>
                    
                    <div class="col-12 mt-3 mb-2">
                      <strong>Status:</strong>
                      <am-select
                        v-model="company.status"
                        :options="statusOptions"
                        class="mt-1"
                      />
                    </div>
                    
                    <!-- Inactive Reason Section -->
                    <div v-if="company.status === 'inactive'" class="col-12 mt-2 mb-2">
                      <strong>Inactive Reason:</strong>
                      <am-select
                        v-model="company.inactive_reason"
                        :options="inactiveReasonOptions"
                        class="mt-1"
                      />
                    </div>
                    
                    <!-- Other Inactive Reason Section -->
                    <div v-if="company.status === 'inactive' && company.inactive_reason === 'other'" class="col-12 mt-2 mb-2">
                      <strong>Please specify:</strong>
                      <b-form-textarea
                        v-model="company.other_inactive_reason"
                        placeholder="Please specify the reason for making this company inactive..."
                        rows="3"
                        class="mt-1"
                      />
                    </div>
                    
                    <!-- Save Status Button -->
                    <div class="col-12 mt-3 mb-2">
                      <am-button
                        label="Save Status"
                        variant="primary"
                        size="sm"
                        :loading="savingStatus && selectedCompanyId === company.id"
                        @click="updateCompanyStatus(company)"
                      />
                    </div>
                  </div>
                </div>
              </b-collapse>
            </b-card>
          </div>
        </div>
      </div>

      <!-- Regional View -->
      <div v-else class="regional-view">
        <div v-if="loadingRegionalData" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading regional data...</span>
          </div>
          <p class="mt-2">Loading regional data...</p>
        </div>
        <div v-else class="row">
          <div class="col-md-8">
            <div class="map-container" :class="{ 'map-fullscreen': isMapFullscreen }">
              <!-- Fullscreen Toggle Button -->
              <div class="map-controls">
                <am-button
                  icon-only
                  variant="primary"
                  :icon="isMapFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'"
                  @click="toggleMapFullscreen"
                  tool-tip-text="Toggle Fullscreen"
                  class="fullscreen-btn"
                />
              </div>
              
              <div v-if="regionalDataWithCoordinates.length === 0" class="text-center py-5">
                <p class="text-muted">No regional data available with coordinates</p>
                <small class="text-muted">Debug: {{ regionalData.length }} regions loaded</small>
              </div>
              <l-map
                v-else
                :zoom="6"
                :center="[7.9465, -1.0232]"
                :style="{ height: isMapFullscreen ? '100vh' : '500px', width: '100%' }"
                :key="`map-${regionalDataWithCoordinates.length}`"
              >
                <l-tile-layer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />
                <l-marker
                  v-for="region in regionalDataWithCoordinates"
                  :key="region.id"
                  :lat-lng="[region.lat, region.lon]"
                >
                  <l-popup>
                    <div>
                      <h6>{{ region.name }}</h6>
                      <p><strong>Cooperatives:</strong> {{ region.total_cooperatives }}</p>
                      <p><strong>Societies:</strong> {{ region.total_societies }}</p>
                      <p><strong>Members:</strong> {{ region.total_members }}</p>
                      <p><strong>Males:</strong> {{ region.total_males }}</p>
                      <p><strong>Females:</strong> {{ region.total_females }}</p>
                      <p><strong>Youth:</strong> {{ region.total_youth }}</p>
                    </div>
                  </l-popup>
                </l-marker>
              </l-map>
            </div>
          </div>
          <div class="col-md-4">
            <div class="regional-stats">
              <h5 class="mb-3">Regional Statistics</h5>
              <div class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Region</th>
                      <th>Cooperatives</th>
                      <th>Societies</th>
                      <th>Members</th>
                      <th>Males</th>
                      <th>Females</th>
                      <th>Youth</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="region in regionalData" :key="region.id">
                      <td>{{ region.name }}</td>
                      <td>{{ region.total_cooperatives }}</td>
                      <td>{{ region.total_societies }}</td>
                      <td>{{ region.total_members }}</td>
                      <td>{{ region.total_males }}</td>
                      <td>{{ region.total_females }}</td>
                      <td>{{ region.total_youth }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="font-weight-bold">
                      <td>Total</td>
                      <td>{{ totalStats.total_cooperatives }}</td>
                      <td>{{ totalStats.total_societies }}</td>
                      <td>{{ totalStats.total_members }}</td>
                      <td>{{ totalStats.total_males }}</td>
                      <td>{{ totalStats.total_females }}</td>
                      <td>{{ totalStats.total_youth }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="displayCompanies.length === 0" class="row">
        <div class="col-12 text-center">
          <div class="empty-state">
            <feather-icon icon="HomeIcon" size="64" class="text-gray mb-3" />
            <h4 class="text-gray">{{ searchQuery ? 'No Companies Found' : 'No Companies Available' }}</h4>
            <p class="text-gray">{{ searchQuery ? 'No companies match your search criteria.' : 'You don\'t have access to any companies at the moment.' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Company Form Modal -->
    <company-form
      :show="showCompanyForm"
      :company="editingCompany"
      @close="closeCompanyForm"
      @company-created="onCompanyCreated"
      @company-updated="onCompanyUpdated"
    />
  </div>
</template>

<script>
import {
  BRow, BCol, BLink, BCard, BCardTitle, BCardText, BImg, BAvatar, BCollapse, BFormCheckbox,
  BBadge, BFormSelect, BFormTextarea,
} from 'bootstrap-vue'
import { LMap, LTileLayer, LMarker, LPopup } from 'vue2-leaflet'
import store from '@/store'
import AmButton from '@/farmbusiness/components/AmButton'
import AmAvatar from '@/farmbusiness/components/AmAvatar'
import AmInput from '@/farmbusiness/components/AmInput'
import CompanyForm from '@/views/auth/CompanyForm.vue'
import showToast from '@/farmbusiness/mixins/showToast'
import http from "@/libs/axios"
import AmSelect from '@/farmbusiness/components/AmSelect'

export default {
  components: {
    AmButton,
    AmAvatar,
    AmInput,
    CompanyForm,
    BRow,
    BCol,
    BLink,
    BCard,
    BCardTitle,
    BCardText,
    BImg,
    BAvatar,
    BCollapse,
    BFormCheckbox,
    BBadge,
    BFormSelect,
    BFormTextarea,
    AmSelect,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
  },
  mixins: [showToast],
  data() {
    return {
      loggingIn: false,
      savingStatus: false,
      selectedCompanyId: null,
      showCompanyForm: false,
      editingCompany: null, // Company being edited
      expandedCompanyId: null, // Track which company is currently expanded
      searchQuery: '',
      searchTimeout: null,
      showAllCompaniesFlag: false,
      // Separate lists for better performance
      originalCompanies: [], // All companies from store
      filteredCompanies: [], // Companies matching search
      displayCompanies: [], // Companies to show (paginated or all)
      isRegionalView: false,
      // Status options
      statusOptions: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],
      inactiveReasonOptions: [
        { value: 'business_close', label: 'Business Close' },
        { value: 'non_compliant', label: 'Non-compliant' },
        { value: 'no_longer_working_with_association', label: 'No longer working with association' },
        { value: 'duplicate_record', label: 'Duplicate record' },
        { value: 'merged_with_another_organization', label: 'Merged with another organization' },
        { value: 'ownership_changed', label: 'Ownership changed' },
        { value: 'sanctioned_by_association', label: 'Sanctioned by association' },
        { value: 'contract_expired', label: 'Contract expired' },
        { value: 'other', label: 'Other (please specify)' }
      ],
      regionalData: [],
      totalStats: {
        total_cooperatives: 0,
        total_societies: 0,
        total_members: 0,
        total_males: 0,
        total_females: 0,
        total_youth: 0,
      },
      loadingRegionalData: false,
      isMapFullscreen: false,
    }
  },
  computed: {
    authUser() {
      return this.$store.getters['auth/user']
    },
    companies() {
      return this.$store.getters['auth/superUserCompanies']
    },
    isSwitching() {
      return this.$route.name === 'switch-company'
    },
    hasMoreCompanies() {
      return this.searchQuery.trim() ? false : (this.originalCompanies.length > 20 && !this.showAllCompaniesFlag)
    },
    remainingCompaniesCount() {
      return this.originalCompanies.length - 20
    },
    regionalDataWithCoordinates() {
      return this.regionalData.filter(region => 
        region.lat !== null && 
        region.lon !== null && 
        !isNaN(region.lat) && 
        !isNaN(region.lon)
      )
    }
  },
  mounted() {
    // Check if user is super user
    if (!this.$store.getters['auth/isSuperUser']) {
      this.$router.push({ name: 'login' })
      return
    }
    
    // Add keyboard listener for escape key
    document.addEventListener('keydown', this.handleKeydown)
    
    // Load companies, countries, classifications, and regions
    Promise.all([
      this.$store.dispatch('auth/getSuperUserCompanies'),
      this.$store.dispatch('auth/getCountries'),
      this.$store.dispatch('auth/getClassificationsForSuperAdmin')
    ]).then(() => {
      this.initializeCompanies()
      this.loadRegionalData()
    }).catch(err => {
      console.log(err)
      this.showError("Failed to load data")
    })
  },
  watch: {
    companies: {
      handler(newCompanies) {
        if (newCompanies && newCompanies.length > 0) {
          this.initializeCompanies()
        }
      },
      immediate: true
    }
  },
  methods: {
    toggleCompanyDetails(company) {
      // If this company is already expanded, collapse it
      if (company.detailsShowing) {
        this.$set(company, 'detailsShowing', false)
        this.expandedCompanyId = null
      } else {
        // Close any other expanded company first
        if (this.expandedCompanyId && this.expandedCompanyId !== company.id) {
          const otherCompany = this.companies.find(c => c.id === this.expandedCompanyId)
          if (otherCompany) {
            this.$set(otherCompany, 'detailsShowing', false)
          }
        }
        // Expand this company
        this.$set(company, 'detailsShowing', true)
        this.expandedCompanyId = company.id
      }
    },
    onCompanyDetailsShow(companyId) {
      this.expandedCompanyId = companyId
    },
    onCompanyDetailsHide(companyId) {
      if (this.expandedCompanyId === companyId) {
        this.expandedCompanyId = null
      }
    },
    loginToCompany(company) {
      // Check if company is active before allowing login
      if (company.status !== 'active') {
        this.showError("Cannot login to inactive company")
        return
      }

      this.loggingIn = true
      this.selectedCompanyId = company.id
      
      this.$store.dispatch('auth/switchCompany', {
        user_id: this.authUser.id,
        company_id: company.id
      }).then(res => {
        this.$store.commit('company/setCompany', res.company)
        this.$store.commit('company/setCurrency', res.currency)
        this.showSuccess('Successfully switched to ' + company.name)
        
        // If this is a switch (not initial login), go back to dashboard
        if (this.$route.name === 'switch-company') {
          this.$router.push('/')
        } else {
          this.$router.push('/')
        }
      }).catch((err) => {
        if (err.response && err.response.status === 401) {
          this.showError(err.response.data || "Company is inactive. Please contact your administrator.")
        } else {
          this.showError("Failed to switch company, please try again")
        }
      }).finally(() => {
        this.loggingIn = false
        this.selectedCompanyId = null
      })
    },
    goBack() {
      if (this.isSwitching) {
        this.$router.push('/')
      } else {
        this.$store.commit('auth/clearAuthState')
        this.$router.push({ name: 'login' })
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    updateCompanyStatus(company) {
      const updateData = {
        companyId: company.id,
        status: company.status,
        inactive_reason: company.status === 'inactive' ? company.inactive_reason : null,
        other_inactive_reason: (company.status === 'inactive' && company.inactive_reason === 'other') ? company.other_inactive_reason : null
      };

      this.savingStatus = true;
      this.selectedCompanyId = company.id;

      this.$store.dispatch('auth/updateCompanyStatus', updateData)
        .then(() => {
          this.showSuccess(`Company status updated to ${company.status === 'active' ? 'Active' : 'Inactive'}`);
          this.onCompanyCreated()
        })
        .catch((err) => {
          this.showError(`Failed to update company status: ${err.message}`);
        })
        .finally(() => {
          this.savingStatus = false;
          this.selectedCompanyId = null;
        });
    },
    addNewCompany() {
      this.editingCompany = null;
      this.showCompanyForm = true;
    },
    editCompany(company) {
      this.editingCompany = company;
      this.showCompanyForm = true;
    },
    closeCompanyForm() {
      this.showCompanyForm = false;
      this.editingCompany = null;
    },
    onCompanyCreated() {
      this.closeCompanyForm();
      this.$store.dispatch('auth/getSuperUserCompanies').catch(err => {
        this.showError("Failed to load companies after adding");
      });
    },
    onCompanyUpdated() {
      this.closeCompanyForm();
      this.$store.dispatch('auth/getSuperUserCompanies').catch(err => {
        this.showError("Failed to load companies after updating");
      });
    },
    initializeCompanies() {
      // Set original companies from store
      this.originalCompanies = [...this.companies]
      this.updateDisplayCompanies()
    },
    updateDisplayCompanies() {
      if (this.searchQuery.trim()) {
        // When searching, show all filtered results
        this.filteredCompanies = this.filterCompanies(this.originalCompanies, this.searchQuery)
        this.displayCompanies = [...this.filteredCompanies]
      } else {
        // When not searching, show paginated results
        this.filteredCompanies = [...this.originalCompanies]
        if (this.showAllCompaniesFlag) {
          this.displayCompanies = [...this.originalCompanies]
        } else {
          this.displayCompanies = this.originalCompanies.slice(0, 20)
        }
      }
    },
    filterCompanies(companies, query) {
      const searchQuery = query.toLowerCase().trim()
      
      return companies.filter(company => {
        // Search in multiple fields
        const searchableFields = [
          company.name,
          company.address,
          company.contact_name,
          company.contact_phone,
          company.secondary_contact,
          company.secondary_contact_phone,
          company.community,
          company.region,
          company.country,
          company.email,
          company.partner_organization,
          company.sustainability_certification,
          company.annual_revenue
        ].filter(Boolean) // Remove null/undefined values
        
        return searchableFields.some(field => 
          field.toString().toLowerCase().includes(searchQuery)
        )
      })
    },
    onSearchInput() {
      // Debounce the search to improve performance
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        // Reset show all flag when searching
        if (this.searchQuery.trim()) {
          this.showAllCompaniesFlag = false
        }
        this.updateDisplayCompanies()
      }, 300)
    },
    clearSearch() {
      this.searchQuery = ''
      this.showAllCompaniesFlag = false
      this.updateDisplayCompanies()
    },
    showAllCompanies() {
      this.showAllCompaniesFlag = true
      this.updateDisplayCompanies()
    },
    async loadRegionalData() {
      this.loadingRegionalData = true
      try {
        console.log('Loading regional data...')
        const data = await this.$store.dispatch('auth/getRegionalData')
        console.log('Regional data received:', data)
        this.regionalData = data.regions || []
        this.totalStats = data.totals || {
          total_cooperatives: 0,
          total_societies: 0,
          total_members: 0,
          total_males: 0,
          total_females: 0,
          total_youth: 0,
        }
        console.log('Regional data set:', this.regionalData)
        console.log('Regions with coordinates:', this.regionalDataWithCoordinates)
      } catch (error) {
        console.error('Failed to load regional data:', error)
        this.showError('Failed to load regional data')
      } finally {
        this.loadingRegionalData = false
      }
    },
    toggleMapFullscreen() {
      this.isMapFullscreen = !this.isMapFullscreen
      if (this.isMapFullscreen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.isMapFullscreen) {
        this.toggleMapFullscreen()
      }
    }
  },
  beforeDestroy() {
    // Reset body overflow when component is destroyed
    document.body.style.overflow = 'auto'
    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleKeydown)
  },
}
</script>

<style lang="scss" scoped>
.choose-company-page {
  padding: 2rem;
  min-height: 100vh;
}

.search-container {
  max-width: 600px;
  margin: 0 auto;
}

.search-input-wrapper {
  position: relative;
  
  // Customize AmInput for search
  .input-group-merge {
    border-radius: 8px;
    overflow: hidden;
    
    .form-control {
      border: 1px solid #dee2e6;
      transition: all 0.3s ease;
      
      &:focus {
        border-color: #007bff;
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      }
    }
    
    .input-group-prepend,
    .input-group-append {
      background-color: transparent;
      border: 1px solid #dee2e6;
      
      .feather-icon {
        color: #6c757d;
        transition: color 0.2s ease;
        
        &:hover {
          color: #007bff;
        }
      }
    }
    
    .input-group-append .feather-icon:hover {
      color: #dc3545;
    }
  }
}

.search-results-info {
  margin-top: 8px;
  text-align: center;
  
  a {
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

// View toggle styles
.view-toggle-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.view-toggle {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 25px;
  padding: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-label {
  padding: 8px 16px;
  font-weight: 500;
  color: #6c757d;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &.active {
    color: #038f03;
    background: white;
    border-radius: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

.toggle-switch {
  margin: 0 8px;
}

// Regional view styles
.regional-view {
  .map-container {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: relative;
    
    &.map-fullscreen {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 9999;
      border-radius: 0;
      box-shadow: none;
    }
    
    .map-controls {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 1000;
      
      .fullscreen-btn {
        background: rgba(255, 255, 255, 0.9);
        border: 1px solid #ddd;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        
        &:hover {
          background: rgba(255, 255, 255, 1);
        }
      }
    }
  }
  
  .regional-stats {
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    height: fit-content;
    
    h5 {
      color: #495057;
      font-weight: 600;
    }
    
    .table {
      margin-bottom: 0;
      
      th {
        background: #f8f9fa;
        border-top: none;
        font-weight: 600;
        font-size: 0.875rem;
        color: #495057;
      }
      
      td {
        font-size: 0.875rem;
        vertical-align: middle;
      }
      
      tfoot tr {
        background: #e9ecef;
        font-weight: 600;
        
        td {
          border-top: 2px solid #dee2e6;
        }
      }
    }
  }
}

// Responsive search styles
@media (max-width: 768px) {
  .search-container {
    max-width: 100%;
  }
  
  .search-input-wrapper .input-group-merge .form-control {
    font-size: 16px; // Prevents zoom on iOS
  }
  
  .regional-view {
    .col-md-8, .col-md-4 {
      margin-bottom: 1rem;
    }
  }
}

.header-container {
  width: 100%;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.header-text {
  flex: 1;
  min-width: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

// Responsive header
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .header-text {
    text-align: center;
  }
  
  .header-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .header-actions .am-button,
  .header-actions .btn {
    flex: 1;
    min-width: 120px;
  }
}

@media (max-width: 480px) {
  .header-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .header-actions .am-button,
  .header-actions .btn {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

.company-details {
  padding: 1rem;
  border-radius: 0.375rem;
  margin-top: 1rem;
}

.empty-state {
  padding: 4rem 2rem;
  
  .feather-icon {
    opacity: 0.5;
  }
}

// Responsive adjustments
@media (max-width: 1200px) {
  .companies-masonry {
    column-count: 2;
  }
}

@media (max-width: 768px) {
  .choose-company-page {
    padding: 1rem;
  }
  
  .company-card {
    .card-body {
      padding: 1rem;
    }
  }
}

// Masonry-like layout using columns
.companies-masonry {
  column-count: 2;
  column-gap: 1.5rem;
  margin-bottom: 2rem;
}

.company-item {
  break-inside: avoid;
  margin-bottom: 1.5rem;
  display: block;
}

// Mobile-first responsive approach
@media (max-width: 768px) {
  .companies-masonry {
    display: flex;
    flex-direction: column;
    column-count: unset;
    column-gap: unset;
  }
  
  .company-item {
    break-inside: unset;
    margin-bottom: 1rem;
    width: 100%;
  }
}

.company-card {
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
}
</style> 